import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { handleError } from "../common/utils";
import { useSignatureSheetService } from "../services/signature-sheet.service";
import { useWorkService } from "../services/work.service";
import {
  WorkSummaryColumn,
  WorkSummaryForSheet,
  WorkSummaryItem,
  WorkStatus,
} from "../types/work.types";
import HeaderNavBar from "./header-nav-bar";
import { Loader } from "./loader";
import Pagination from "./pagination-control";

const STATUS_ABBREVIATIONS: Record<number, string> = {
  [WorkStatus.None]: "N",
  [WorkStatus.Flagged]: "F",
  [WorkStatus.Break]: "B",
  [WorkStatus.Completed]: "C",
  [WorkStatus.Assigned]: "A",
};

function statusAbbreviation(status: WorkStatus): string {
  return STATUS_ABBREVIATIONS[status] ?? "?";
}

function WorkLink({ item }: { item: WorkSummaryItem }) {
  return (
    <a href={`/work/${item.workId}`}>
      W-{item.workId}({statusAbbreviation(item.workStatus)})
    </a>
  );
}

export default function WorkBySheet() {
  const { matterId } = useParams();
  const [sheetNumbers, setSheetNumbers] = useState<number[]>([]);
  const [currentSheetNumber, setCurrentSheetNumber] = useState<number | null>(
    null
  );
  const [workSummary, setWorkSummary] = useState<WorkSummaryForSheet | null>(
    null
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const { getSheetNumbersByMatter } = useSignatureSheetService();
  const { getWorkSummaryForSheet } = useWorkService();

  useEffect(() => {
    setCurrentSheetNumber(null);
    setWorkSummary(null);
    if (!matterId) return;
    getSheetNumbersByMatter(matterId).then(
      (numbers) => {
        setSheetNumbers(numbers);
        if (numbers.length > 0) {
          setCurrentSheetNumber(numbers[0]);
        }
      },
      (err) => handleError(err, setError)
    );
  }, [matterId, getSheetNumbersByMatter]);

  const loadWorkSummary = useCallback(
    (sheetNumber: number) => {
      setError("");
      setLoading(true);
      getWorkSummaryForSheet(matterId!, sheetNumber).then(
        (data) => {
          setWorkSummary(data);
          setLoading(false);
        },
        (err) => {
          handleError(err, setError, setLoading);
        }
      );
    },
    [matterId, getWorkSummaryForSheet]
  );

  useEffect(() => {
    if (currentSheetNumber !== null) {
      loadWorkSummary(currentSheetNumber);
    }
  }, [currentSheetNumber, loadWorkSummary]);

  const tableLayout = useMemo(() => {
    if (!workSummary) return null;

    const cols: WorkSummaryColumn[] = workSummary.columns
      .slice()
      .sort((a, b) => a.columnIndex - b.columnIndex);

    // Group work items by row number
    const byRow = new Map<number, WorkSummaryItem[]>();
    for (const item of workSummary.workItems) {
      if (item.rowNumber === 0) continue;
      const existing = byRow.get(item.rowNumber) ?? [];
      existing.push(item);
      byRow.set(item.rowNumber, existing);
    }

    const rowNumbers = Array.from(byRow.keys()).sort((a, b) => a - b);

    const rows = rowNumbers.flatMap((rowNum) => {
      const items = byRow.get(rowNum)!;
      if (cols.length === 0) return [];

      // For each column position, collect items covering it, sorted by workId
      const perCol: WorkSummaryItem[][] = cols.map(() => []);
      for (const item of items) {
        if (item.firstColumnIndex == null || item.lastColumnIndex == null) continue;
        for (let i = 0; i < cols.length; i++) {
          const ci = cols[i].columnIndex;
          if (ci >= item.firstColumnIndex && ci <= item.lastColumnIndex) {
            perCol[i].push(item);
          }
        }
      }
      for (const arr of perCol) arr.sort((a, b) => a.workId - b.workId);

      const subRowCount = Math.max(
        1,
        ...perCol.map((items) => items.length)
      );

      // Precompute cells for each sub-row
      type Cell = {
        key: number;
        item: WorkSummaryItem | null;
        colSpan?: number;
        rowSpan?: number;
      };
      const subRows: Cell[][] = [];

      for (let sr = 0; sr < subRowCount; sr++) {
        const cells: Cell[] = [];
        let ci = 0;
        while (ci < cols.length) {
          const items = perCol[ci];
          const isSingle = items.length <= 1;
          const item = isSingle ? items[0] ?? null : items[sr] ?? null;

          // Compute colSpan: how many consecutive columns share this work item
          let span = 1;
          let allSingle = isSingle;
          if (item) {
            for (let j = ci + 1; j < cols.length; j++) {
              const nItems = perCol[j];
              const nSingle = nItems.length <= 1;
              const nItem = nSingle ? nItems[0] ?? null : nItems[sr] ?? null;
              if (nItem && nItem.workId === item.workId) {
                span++;
                if (!nSingle) allSingle = false;
              } else {
                break;
              }
            }
          } else {
            // Empty cells: merge consecutive empties with same single/multi status
            for (let j = ci + 1; j < cols.length; j++) {
              const nItems = perCol[j];
              const nSingle = nItems.length <= 1;
              const nItem = nSingle ? nItems[0] ?? null : nItems[sr] ?? null;
              if (nItem === null && nSingle === isSingle) {
                span++;
                if (!nSingle) allSingle = false;
              } else {
                break;
              }
            }
          }

          if (allSingle) {
            // rowSpan cell — only emit on sub-row 0
            if (sr === 0) {
              cells.push({
                key: cols[ci].columnIndex,
                item,
                colSpan: span > 1 ? span : undefined,
                rowSpan: subRowCount > 1 ? subRowCount : undefined,
              });
            }
          } else {
            cells.push({
              key: cols[ci].columnIndex,
              item,
              colSpan: span > 1 ? span : undefined,
            });
          }

          ci += span;
        }
        subRows.push(cells);
      }

      return [{ rowNum, subRowCount, subRows }];
    });

    return { cols, rows };
  }, [workSummary]);

  return (
    <div className="container">
      <HeaderNavBar backTo="details" shouldHideMatter={false} />
      <div className="col-sm-12">
        {sheetNumbers.length > 0 && currentSheetNumber !== null && (
          <Pagination
            sheetNumbers={sheetNumbers}
            currentSheetNumber={currentSheetNumber}
            onNavigate={setCurrentSheetNumber}
          />
        )}

        {loading && (
          <div className="mt-3">
            <Loader />
          </div>
        )}

        {error && (
          <div className="alert alert-danger mt-3" role="alert">
            {error}
          </div>
        )}

        {tableLayout && !loading && (
          <table className="table table-bordered table-sm mt-3 text-center">
            <thead>
              <tr>
                <th>Row</th>
                {tableLayout.cols.map((col) => (
                  <th key={col.columnIndex}>{col.name}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {tableLayout.rows.length === 0 ? (
                <tr>
                  <td
                    colSpan={tableLayout.cols.length + 1}
                    className="text-center"
                  >
                    No work items for this sheet
                  </td>
                </tr>
              ) : (
                tableLayout.rows.map(
                  ({ rowNum, subRowCount, subRows }) =>
                    subRows.map((cells, subRow) => (
                      <tr
                        key={`${rowNum}-${subRow}`}
                        style={
                          subRow === 0
                            ? { borderTopWidth: "2px", borderTopColor: "#000" }
                            : undefined
                        }
                      >
                        {subRow === 0 && (
                          <td rowSpan={subRowCount}>{rowNum}</td>
                        )}
                        {cells.map((cell) => (
                          <td
                            key={cell.key}
                            colSpan={cell.colSpan}
                            rowSpan={cell.rowSpan}
                          >
                            {cell.item && <WorkLink item={cell.item} />}
                          </td>
                        ))}
                      </tr>
                    ))
                )
              )}
            </tbody>
          </table>
        )}

        {sheetNumbers.length === 0 && !loading && (
          <div className="row">
            <div className="col">No sheets available</div>
          </div>
        )}
      </div>
    </div>
  );
}
